import React from "react";
import { Plus, RocketLaunch } from "@phosphor-icons/react";
import MoneyInput from "@/components/MoneyInput";
import InfoTip from "@/components/onboarding/InfoTip";
import CollapsibleCard from "@/components/onboarding/CollapsibleCard";
import VatLearnMore from "@/components/onboarding/VatLearnMore";
import { useOpenCard } from "@/components/onboarding/useOpenCard";
import { monthNames } from "@/lib/costHelpers";
import { isOther, sellMode, streamComplete, streamHeader, isLaunch, launchMonthIndex } from "@/lib/incomeHelpers";
import { formatGBP } from "@/lib/brand";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TYPES = [
  { v: "service", label: "Service / 1:1" },
  { v: "product", label: "Product" },
  { v: "one_off_project", label: "One-off project" },
  { v: "other", label: "Other" },
];

const Label = ({ children }) => <label className="block text-xs font-semibold mb-1">{children}</label>;

const TypePicker = ({ r, i, onPatch }) => (
  <div>
    <Label>
      What kind of income stream is this?
      <InfoTip testid={`offer-type-tip-${i}`}>
        <ul className="list-disc pl-4 space-y-1">
          <li><strong>Service / 1:1:</strong> coaching, consulting or done-for-you work, priced per client or session</li>
          <li><strong>Product:</strong> things you sell, e.g. courses, templates, prints</li>
          <li><strong>One-off project:</strong> fixed-price work, e.g. a website build</li>
          <li><strong>Other:</strong> money that comes in without a price tag, e.g. book royalties, affiliate commission, cashback or interest</li>
        </ul>
      </InfoTip>
    </Label>
    <div className="flex flex-wrap gap-2">
      {TYPES.map((t) => (
        <button
          key={t.v}
          type="button"
          onClick={() =>
            onPatch(
              t.v === "other"
                ? { offer_type: "other", clients_per_month: 1, is_future: false, launch_month_index: 0, monthly_sales: null }
                : { offer_type: t.v, monthly_amounts: null }
            )
          }
          data-testid={`offer-type-${i}-${t.v}`}
          className={`px-3 py-2 rounded-xl border-[1.5px] border-black text-sm font-semibold transition-colors ${(r.offer_type || "service") === t.v ? "bg-[#FFBD4A]" : "bg-white hover:bg-[#FFF7E5]"}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  </div>
);

const MonthBoxes = ({ i, startIso, values, money, price, onChange }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2" data-testid={`offer-custom-grid-${i}`}>
    {monthNames(startIso).map((m, k) => (
      <div key={k} className="rounded-lg border-[1.5px] border-black/60 p-2">
        <div className="text-xs font-heading uppercase tracking-wide mb-1">{m}</div>
        {money ? (
          <MoneyInput value={values[k]} onChange={(n) => onChange(k, n)} testid={`offer-month-amount-${i}-${k}`} />
        ) : (
          <>
            <input
              className="input"
              inputMode="numeric"
              value={values[k]}
              onChange={(e) => onChange(k, Math.max(0, Number(e.target.value.replace(/[^\d.]/g, "")) || 0))}
              data-testid={`offer-month-sales-${i}-${k}`}
              aria-label={`Sales in ${m}`}
            />
            <div className="text-xs text-black/65 mt-1">sales = {formatGBP((Number(values[k]) || 0) * price)}</div>
          </>
        )}
      </div>
    ))}
  </div>
);

const OfferFields = ({ r, i, startIso, onPatch }) => {
  const mode = sellMode(r);
  const price = Number(r.price) || 0;
  const long = monthNames(startIso, true);
  const setMode = (m) => {
    if (m === "every") onPatch({ is_future: false, launch_month_index: 0, monthly_sales: null });
    if (m === "from") onPatch({ is_future: true, launch_month_index: Math.max(1, Number(r.launch_month_index) || 1), monthly_sales: null });
    if (m === "custom") onPatch({ is_future: false, monthly_sales: Array(12).fill(Number(r.clients_per_month) || 0) });
  };
  return (
    <>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>
            {r.offer_type === "one_off_project" ? "Project price (£)" : "Price per sale (£)"}
            <InfoTip testid={`offer-price-tip-${i}`}>What the customer pays you, including VAT if you charge it.</InfoTip>
          </Label>
          <MoneyInput value={r.price} onChange={(n) => onPatch({ price: n })} testid={`offer-price-${i}`} placeholder="350" />
        </div>
        {mode !== "custom" && (
          <div>
            <Label>
              Sales per month
              <InfoTip testid={`offer-sales-tip-${i}`}>How many you realistically expect to sell in a typical month, not your best month.</InfoTip>
            </Label>
            <input className="input" inputMode="numeric" value={r.clients_per_month} onChange={(e) => onPatch({ clients_per_month: Math.max(0, Number(e.target.value.replace(/[^\d.]/g, "")) || 0) })} data-testid={`offer-clients-${i}`} />
          </div>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div>
          <Label>
            When do you sell it?
            <InfoTip testid={`offer-when-tip-${i}`}>Launched but no customers yet, or still planning it? Pick the month you realistically expect your first sale. Busy months, holidays or a launch ramp-up? Choose Custom months &amp; sales to set each month.</InfoTip>
          </Label>
          <select className="input" value={mode} onChange={(e) => setMode(e.target.value)} data-testid={`offer-when-${i}`}>
            <option value="every">Every month</option>
            <option value="from">Every month, from…</option>
            <option value="custom">Custom months &amp; sales</option>
          </select>
        </div>
        {mode === "from" && (
          <div>
            <Label>First sale expected in</Label>
            <select className="input" value={r.launch_month_index} onChange={(e) => onPatch({ launch_month_index: Number(e.target.value) })} data-testid={`offer-launch-month-${i}`}>
              {long.slice(1).map((m, k) => (
                <option key={k + 1} value={k + 1}>{m}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      {mode === "custom" && (
        <MonthBoxes i={i} startIso={startIso} values={r.monthly_sales} price={price} onChange={(k, n) => onPatch({ monthly_sales: r.monthly_sales.map((v, j) => (j === k ? n : v)) })} />
      )}
    </>
  );
};

const OtherFields = ({ r, i, startIso, onPatch }) => {
  const custom = Array.isArray(r.monthly_amounts);
  return (
    <>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>
            Regular amount (£)
            <InfoTip testid={`other-amount-tip-${i}`}>Your typical monthly amount. If it varies, choose 'Custom months &amp; amounts' under How often?</InfoTip>
          </Label>
          <MoneyInput value={r.price} onChange={(n) => onPatch({ price: n })} testid={`offer-price-${i}`} placeholder="80" />
        </div>
        <div>
          <Label>How often?</Label>
          <select className="input" value={custom ? "custom" : "every"} onChange={(e) => onPatch({ monthly_amounts: e.target.value === "custom" ? Array(12).fill(Number(r.price) || 0) : null })} data-testid={`other-freq-${i}`}>
            <option value="every">Every month</option>
            <option value="custom">Custom months &amp; amounts</option>
          </select>
        </div>
      </div>
      {custom && <MonthBoxes money i={i} startIso={startIso} values={r.monthly_amounts} onChange={(k, n) => onPatch({ monthly_amounts: r.monthly_amounts.map((v, j) => (j === k ? n : v)) })} />}
    </>
  );
};

const LaunchNote = ({ r, i, startIso, onDetour }) => (
  <div className="ai-insight mt-4" data-testid={`launch-note-${i}`}>
    <p className="flex items-start gap-2">
      <RocketLaunch size={18} weight="duotone" className="shrink-0 mt-0.5" />
      <span>
        <strong>Launching this in {monthNames(startIso, true)[launchMonthIndex(r)].split(" ")[0]}?</strong> Launches often come with extra costs beforehand, e.g. paid ads or a new software tool. If you haven't already, add them to your costs.
      </span>
    </p>
    <div className="flex flex-wrap gap-4 mt-2 text-sm">
      <button type="button" className="underline font-semibold" onClick={() => onDetour("recurring_costs", r.id)} data-testid={`launch-to-regular-${i}`}>Take me to Regular costs</button>
      <button type="button" className="underline font-semibold" onClick={() => onDetour("one_off", r.id)} data-testid={`launch-to-oneoff-${i}`}>Take me to One-off costs</button>
    </div>
  </div>
);

export const IncomeStep = ({ streams, startIso, vat, focusId, makeItem, onAdd, onPatch, onRemove, onDetour }) => {
  const card = useOpenCard(streams, streamComplete);
  React.useEffect(() => {
    if (focusId) card.openCard(focusId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId]);
  return (
    <div className="space-y-5" data-testid="income-step">
      <p className="text-black/80 leading-relaxed">
        Now for the exciting bit: looking ahead. This isn't what you've already sold. It's what you <strong>plan and expect to bring in over the next 12 months</strong>.
      </p>
      <ul className="list-disc pl-5 space-y-2 text-black/80 leading-relaxed">
        <li>Add each income stream: what you sell now, what you've launched but not sold yet, or what you're planning to launch.</li>
        <li>For each one, add the price and how many you realistically expect to sell.</li>
        <li>Busy or quiet months? Choose <strong>Custom months &amp; sales</strong> to set each month.</li>
      </ul>
      <p className="text-black/80 leading-relaxed">Nothing's set in stone: you can refine everything later. A rough plan is the perfect start.</p>
      <VatLearnMore vat={vat} testid="revenue-vat" />

      {streams.length === 0 && <p className="text-black/60" data-testid="offer-empty">No income streams yet.</p>}
      {streams.map((r, i) => (
        <CollapsibleCard key={r.id} testid={`offer-row-${i}`} title={streamHeader(r, i, startIso)} open={card.openId === r.id} nudge={card.nudgeId === r.id} onToggle={() => card.toggle(r.id)} onRemove={() => onRemove(i)}>
          <div className="space-y-4">
            <TypePicker r={r} i={i} onPatch={(p) => onPatch(i, p)} />
            <div>
              <Label>What is it?</Label>
              <input className="input" value={r.name} onChange={(e) => onPatch(i, { name: e.target.value })} placeholder={isOther(r) ? "e.g. Book royalties" : "e.g. 1:1 Coaching"} data-testid={`offer-name-${i}`} maxLength={120} />
            </div>
            {isOther(r) ? <OtherFields r={r} i={i} startIso={startIso} onPatch={(p) => onPatch(i, p)} /> : <OfferFields r={r} i={i} startIso={startIso} onPatch={(p) => onPatch(i, p)} />}
            {isLaunch(r) && <LaunchNote r={r} i={i} startIso={startIso} onDetour={onDetour} />}
          </div>
        </CollapsibleCard>
      ))}
      <button type="button" className="btn-secondary" onClick={() => card.tryAdd(makeItem(), onAdd)} data-testid="offer-add">
        <Plus size={16} weight="bold" /> Add {streams.length ? "another" : "an"} income stream
      </button>
    </div>
  );
};

export const NoIncomeDialog = ({ open, onCarryOn, onAdd }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onAdd()}>
    <DialogContent className="max-w-lg bg-white border-[1.5px] border-black rounded-2xl" data-testid="no-income-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading uppercase text-2xl">No income added yet</DialogTitle>
        <DialogDescription className="text-black/80 text-base leading-relaxed pt-2">
          You haven't added any income yet, so your forecast will only show money going out. That's okay if you're just starting; you can add income any time.
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
        <button type="button" className="btn-primary" onClick={onAdd} data-testid="no-income-add-btn">Add income</button>
        <button type="button" className="text-sm underline font-semibold" onClick={onCarryOn} data-testid="no-income-carry-on-btn">Carry on</button>
      </div>
    </DialogContent>
  </Dialog>
);
